/**
 * Отдельный композер для треда.
 *
 * Вынесен в свой компонент, потому что тредовый композер отличается от
 * комнатного тремя вещами:
 *  1. У него НЕТ кнопки вложения (файлы в тредах — за фичефлагом, план J.4.3);
 *  2. У него НЕТ drag-and-drop (по той же причине);
 *  3. Черновик хранится ПО ТРЕДУ (ключ alg.draft.<roomId>.<threadId>,
 *     Антон 2026-09-16 21:26 UTC): при смене треда восстанавливается
 *     свой текст, при смене КОМНАТЫ панель размонтируется целиком, и
 *     черновик дописывается синхронно из cleanup-эффекта.
 *
 * Общая логика (текст, упоминания, автодополнение) скопирована из
 * Composer.tsx сознательно, а не вынесена в общий хук: различий слишком
 * много (вложения, баннеры, сброс при смене roomId), и попытка
 * переиспользовать родительский композер привела бы к лесу пропсов.
 *
 * ПРАВИЛО АНТОНА (2026-09-10): логику автодополнения @ НЕ переписываем —
 * формат «@Имя (@login)», закрытие по клику мимо. Всё это сохранено ниже
 * буквально, без изменений.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useT, useTF } from "../i18n";

import type { MemberInfo, RichSegment } from "../matrix/roomActions";
import { palette, s } from "./styles";
import { color, font, radius, space } from "./theme";
import { draftKey, loadDraft, saveDraft, clearDraft } from "../matrix/drafts";
import { useTypingSender } from "../matrix/typing";
import { insertMention, useCaretAfterCommit } from "./mentionInsert";
import type { MatrixClient } from "matrix-js-sdk";

/** Одно подтверждённое упоминание в черновике (id null = @room). */
interface DraftMention {
  userId: string | null;
  displayName: string;
  length: number;
}

/**
 * Разбить текст черновика на сегменты, подставив подтверждённые упоминания.
 * Сканируем `mentions` в порядке вставки: для каждого ищем его `displayName`
 * в необработанной части текста и вырезаем текст до него. Совпадение, которое
 * пользователь стёр или переписал, просто не находится — и такое упоминание
 * тихо выпадает (push не отправляется), не ломая остальное.
 */
function buildSegments(text: string, mentions: DraftMention[]): RichSegment[] {
  if (mentions.length === 0) return [{ type: "text", text }];

  const segments: RichSegment[] = [];
  let cursor = 0;
  for (const m of mentions) {
    const idx = text.indexOf(m.displayName, cursor);
    if (idx < 0) continue;
    if (idx > cursor) segments.push({ type: "text", text: text.slice(cursor, idx) });
    segments.push({ type: "mention", userId: m.userId, displayName: m.displayName });
    cursor = idx + m.length;
  }
  if (cursor < text.length) segments.push({ type: "text", text: text.slice(cursor) });
  return segments;
}

function hasContent(text: string, mentions: DraftMention[]): boolean {
  return text.trim().length > 0 || mentions.length > 0;
}

export function ThreadComposer({
  disabled,
  threadId,
  roomId,
  client,
  members,
  canMentionRoom,
  onSend,
  replyTo,
  onCancelReply,
}: {
  disabled: boolean;
  /** id корня треда — сбрасываем черновик при смене треда. */
  threadId: string;
  /**
   * id комнаты — нужен для ключа черновика `alg.draft.<roomId>.<threadId>`
   * и для отправки typing-уведомлений (typing общий на комнату, Антон
   * 2026-09-16 21:26–21:27 UTC).
   */
  roomId: string;
  client: MatrixClient | null;
  members: MemberInfo[];
  canMentionRoom: boolean;
  onSend: (segments: RichSegment[]) => Promise<void>;
  /** Если задан — композер в режиме ответа на сообщение. */
  replyTo?: { eventId: string; senderName: string; snippet: string } | null;
  onCancelReply?: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  /** Ключ черновика этого треда в localStorage. */
  const storageKey = draftKey(roomId, threadId);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState<DraftMention[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mention, setMention] = useState<{
    start: number;
    query: string;
    activeIndex: number;
  } | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Смена ТРЕДА — восстанавливаем ЧЕРНОВИК этого треда (Антон,
  // 2026-09-16 21:26 UTC), а не стираем набранное. Раньше здесь было
  // setText("") — переключение между тредами убивало текст. Черновик
  // лежит в localStorage по ключу alg.draft.<roomId>.<threadId>.
  // Смену КОМНАТЫ ловит родитель (размонтирование) — cleanup-эффект
  // ниже дописывает черновик синхронно.
  // При открытии треда фокус сразу уходит в поле ввода, чтобы человек
  // мог печатать без лишнего клика.
  useEffect(() => {
    setText(loadDraft(storageKey));
    setMentions([]);
    setMention(null);
    setError(null);
    ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, roomId]);

  // Сохранение черновика с debounce 300 мс (постановка).
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Последний text для синхронного сброса при смене треда/unmount. */
  const latestText = useRef(text);
  latestText.current = text;
  useEffect(() => {
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      draftSaveTimer.current = null;
      saveDraft(storageKey, text);
    }, 300);
    return () => {
      if (draftSaveTimer.current) {
        clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
    };
  }, [storageKey, text]);

  // Cleanup debounce-эффекта отменяет ожидавший таймер: последние <300 мс
  // ввода при закрытии треда/перезагрузке потерялись бы — дописываем
  // черновик синхронно.
  useEffect(() => {
    return () => saveDraft(storageKey, latestText.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Индикатор набора. Typing общий на комнату (протокол не различает
  // композер треда), поэтому шлём в roomId (см. matrix/typing.ts).
  const typing = useTypingSender(client, roomId);
  /** Каретка после выбора упоминания — ставится после коммита `text`. */
  const caretAfterCommit = useCaretAfterCommit(ref, text);

  // При входе в режим ОТВЕТА фокус сразу уходит в поле ввода.
  // Ждём рендер плашки, иначе фокус слетит.
  useEffect(() => {
    if (!replyTo) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [replyTo]);

  // Клик МИМО списка закрывает автодополнение (Антон, 2026-09-10 11:24).
  // Текст НЕ трогаем: закрываем только список, набранное остаётся.
  useEffect(() => {
    if (!mention) return;
    const onOutside = (e: PointerEvent) => {
      const root = rootRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) {
        setMention(null);
      }
    };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, [mention]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  const candidates = useMemo(() => {
    const q = mention?.query.toLowerCase() ?? "";
    const list: {
      userId: string | null;
      displayName: string;
      name: string;
      subtitle: string;
      invited: boolean;
    }[] = [];

    if (canMentionRoom && "room".startsWith(q)) {
      list.push({
        userId: null,
        displayName: "@room",
        name: t("composer.mentionAll"),
        subtitle: "",
        invited: false,
      });
    }
    for (const mm of members) {
      const name = mm.name;
      const hay = `${name} ${mm.userId}`.toLowerCase();
      if (hay.includes(q)) {
        const hasName = name !== mm.userId;
        list.push({
          userId: mm.userId,
          displayName: hasName ? `@${name}` : mm.userId,
          name,
          subtitle: hasName ? `(${mm.userId})` : "",
          invited: mm.membership === "invite",
        });
      }
    }
    list.sort((a, b) => Number(a.invited) - Number(b.invited));
    return list.slice(0, 8);
  }, [mention?.query, members, canMentionRoom]);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    caretAfterCommit.cancel();
    const value = e.target.value;
    setText(value);
    // Индикатор набора у собеседников (троттлинг внутри; пустое поле — стоп).
    typing.noteText(value);

    const caret = e.target.selectionStart ?? value.length;
    const lastBreak = Math.max(
      value.lastIndexOf(" ", caret - 1),
      value.lastIndexOf("\n", caret - 1),
    );
    const at = value.lastIndexOf("@", caret - 1);
    if (at > lastBreak) {
      const query = value.slice(at + 1, caret);
      if (!query.includes(" ") && !query.includes("\n")) {
        setMention({ start: at, query, activeIndex: 0 });
        return;
      }
    }
    setMention(null);
  };

  // Каретка — в конец имени + ровно один пробел, после коммита состояния
  // (Антон, 2026-10-16 09:50). Логика общая с Composer: mentionInsert.ts.
  const pickMention = (c: { userId: string | null; displayName: string }) => {
    if (!mention) return;
    const r = insertMention(text, mention.start, mention.query, c.displayName);
    setText(r.text);
    typing.noteText(r.text);
    setMentions((prev) => [
      ...prev,
      { userId: c.userId, displayName: c.displayName, length: c.displayName.length },
    ]);
    setMention(null);
    caretAfterCommit.request(r.caret);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && candidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMention((m) => (m ? { ...m, activeIndex: (m.activeIndex + 1) % candidates.length } : m));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMention((m) =>
          m ? { ...m, activeIndex: (m.activeIndex - 1 + candidates.length) % candidates.length } : m,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(candidates[mention.activeIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }

    // Enter ОТПРАВЛЯЕТ сообщение только на десктопе (Антон 05.10):
    // на тач-устройствах (телефон/планшет) чистый Enter — перенос строки,
    // отправка ТОЛЬКО кнопкой-стрелкой (как в iOS). На десктопе — отправка.
    // Признак тач-устройства: pointer:coarse или maxTouchPoints>0 — тот же,
    // что используется в Composer.tsx для фокуса при входе в комнату.
    const touchPrimary =
      (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) ||
      navigator.maxTouchPoints > 0;
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && !touchPrimary) {
      e.preventDefault();
      void submit();
      return;
    }

    // На тач-устройстве Enter — перенос строки: preventDefault НЕТ,
    // браузер сам вставит \n (та же логика, что в основном композере).
  };

  const submit = async () => {
    if (!hasContent(text, mentions) || busy) return;
    const segments = buildSegments(text, mentions);
    setText("");
    setMentions([]);
    setMention(null);
    setError(null);
    setBusy(true);
    try {
      await onSend(segments);
      // Успешная отправка: черновик треда отработан, «печатает…» гасим.
      clearDraft(storageKey);
      typing.stop();
      if (replyTo) onCancelReply?.();
    } catch (e) {
      setError((e as Error).message);
      setText(text);
      setMentions(mentions);
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };

  const sendable = hasContent(text, mentions);

  return (
    <div style={{ flex: "0 0 auto" }} ref={rootRef}>
      {replyTo && (
        <div className="alg-composer-banner" data-testid="thread-reply-banner">
          <div className="alg-composer-banner-body">
            <div className="alg-composer-banner-title">{tfmt("composer.reply", { user: replyTo.senderName })}</div>
            <div className="alg-composer-banner-snippet">{replyTo.snippet}</div>
          </div>
          <button
            type="button"
            className="alg-composer-banner-close"
            onClick={onCancelReply}
            data-testid="thread-reply-cancel"
            title={t("composer.cancelReply")}
          >
            ×
          </button>
        </div>
      )}
      {mentions.length > 0 && (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 14px 0" }}
          data-testid="thread-mention-pills"
        >
          {mentions.map((m, i) => (
            <span
              key={`${m.userId ?? "room"}-${i}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: radius.pill,
                background: color.accentSoft,
                color: color.accent,
                fontSize: font.tiny,
              }}
            >
              {m.displayName}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div style={{ padding: "6px 14px", fontSize: 12, color: palette.danger }} role="alert">
          {tfmt("composer.sendError", { error })}
        </div>
      )}

      {mention && candidates.length > 0 && (
        <div
          style={{
            margin: "0 14px",
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            background: color.bg,
            boxShadow: "0 4px 16px rgba(20,24,31,0.14)",
            maxHeight: 240,
            overflowY: "auto",
          }}
          data-testid="thread-mention-list"
        >
          {candidates.map((c, i) => (
            <button
              key={c.displayName}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pickMention(c);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: space.md,
                width: "100%",
                padding: "8px 12px",
                border: "none",
                background: i === mention.activeIndex ? color.accentSoft : "transparent",
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
              }}
              data-testid={`thread-mention-item-${c.userId ?? "room"}`}
            >
              <span
                style={{
                  minWidth: 0,
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    fontWeight: font.weightBold,
                    color: color.text,
                    flex: "0 1 auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.displayName}
                </span>
                {c.subtitle && (
                  <span
                    style={{
                      fontSize: font.tiny,
                      color: palette.textDim,
                      flex: "0 1 auto",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.subtitle}
                  </span>
                )}
              </span>
              {c.invited && (
                <span
                  style={{ fontSize: font.tiny, color: palette.textDim, flex: "0 0 auto" }}
                  title={t("ui.priglash_n_no_esch_ne_vosh_l_uvedomlenie_pri")}
                >{t("composer.mentionInvited")}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div style={s.composer}>
        <textarea
          ref={ref}
          style={s.textarea}
          value={text}
          disabled={disabled}
          placeholder={
            disabled
              ? t("composer.disabledPlaceholder")
              : ""
          }
          data-testid="thread-composer"
          rows={1}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onCompositionEnd={() => caretAfterCommit.confirm()}
        />
        <button
          type="button"
          style={{ ...s.button, opacity: disabled || !sendable ? 0.5 : 1 }}
          disabled={disabled || !sendable || busy}
          onClick={() => void submit()}
          data-testid="thread-send"
        >{t("composer.sendButton")}</button>
      </div>
    </div>
  );
}
